import React from 'react';
import classnames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import { ZoneType, InstallableModuleType } from '../../../../../types/layout';
import { ModuleType } from '../../../../../types/modules';
import { WithSectionContextProps } from '../../../Sections/SectionContext';

import s from './WidgetTypeCard.scss';

export interface Props extends WithSectionContextProps, InstallableModuleType {
  zone: ZoneType;
  className?: string;
  hasCoreMembership?: boolean;
}

const WidgetTypeCard: React.FC<Props> = ({
  className,
  zone,
  limit,
  moduleName,
  restrict,
  installedCount = 0,
  hasCoreMembership,
}) => {
  const { t } = useTranslation();
  const coreLimit = restrict && restrict.priv === 'core' ? restrict.limit : 0;
  const isCoreOnlyModule = !!coreLimit;
  const isLocked =
    isCoreOnlyModule && !hasCoreMembership && installedCount >= limit;

  let title;
  let description;
  let miniatureClassname;
  switch (moduleName) {
    case ModuleType.EXHIBITIONIST:
      if (zone === ZoneType.TOP_BAR) {
        title = t('widgets.exhibitionist.scroller.addButtonHeader');
        description = t('widgets.exhibitionist.scroller.description');
        miniatureClassname = 'exhibitionist-slider';
      } else {
        title = t('widgets.exhibitionist.gridGallery.addButtonHeader');
        description = t('widgets.exhibitionist.gridGallery.description');
        miniatureClassname = 'exhibitionist-grid';
      }
      break;
    case ModuleType.WATCHERS:
      title = t('widgets.watchers.addButtonHeader');
      description = t('widgets.watchers.description');
      miniatureClassname = 'watchers';
      break;
    case ModuleType.WATCHING:
      title = t('widgets.watching.addButtonHeader');
      description = t('widgets.watching.description');
      miniatureClassname = 'watching';
      break;
    case ModuleType.GROUPS_MEMBER:
      title = t('widgets.groups_member.addButtonHeader');
      description = t('widgets.groups_member.description');
      miniatureClassname = 'groups-member';
      break;
    case ModuleType.GROUPS_ADMIN:
      title = t('widgets.groups_admin.addButtonHeader');
      description = t('widgets.groups_admin.description');
      miniatureClassname = 'groups-admin';
      break;
    case ModuleType.DONATIONS:
      title = t('widgets.donations.addButtonHeader');
      description = t('widgets.donations.description');
      miniatureClassname = 'donations';
      break;
    case ModuleType.DEVIOUSNESS:
      title = t('widgets.deviousness.addButtonHeader');
      description = t('widgets.deviousness.description');
      miniatureClassname = 'deviousness';
      break;
    case ModuleType.FEED:
      title = t('widgets.feed.addButtonHeader');
      description = t('widgets.feed.description');
      miniatureClassname = 'feed';
      break;
    case ModuleType.MY_COMMENTS:
      title = t('widgets.myComments.addButtonHeader');
      description = t('widgets.myComments.description');
      miniatureClassname = 'my-comments';
      break;
    case ModuleType.COMMENTS:
      title = t('widgets.profileComments.addButtonHeader');
      description = t('widgets.profileComments.description');
      miniatureClassname = 'profile-comments';
      break;
    case ModuleType.BADGES_ACTIVITY:
      title = t('widgets.myBadges.addButtonHeader');
      description = t('widgets.myBadges.description');
      miniatureClassname = 'my-badges';
      break;
    case ModuleType.STATS:
      title = t('widgets.statistics.addButtonHeader');
      description = t('widgets.statistics.description');
      miniatureClassname = 'stats';
      break;
    case ModuleType.VISITORS:
      title = t('widgets.visitors.addButtonHeader');
      description = t('widgets.visitors.description');
      miniatureClassname = 'visitors';
      break;
    case ModuleType.FEATURED_DEVIATION:
      title = t('widgets.featured_deviation.addButtonHeader');
      description = t('widgets.featured_deviation.description');
      miniatureClassname = 'featured-deviation';
      break;
    case ModuleType.CUSTOM:
      title = t!('widgets.custombox.addButtonHeader');
      description = t!('widgets.custombox.description');
      miniatureClassname = 'custombox';
      break;
    default:
      title = moduleName;
      description = 'no widget type card';
  }

  return (
    <div
      className={classnames(
        s['root'],
        isCoreOnlyModule && s['core-only'],
        className
      )}
    >
      <div
        className={classnames(
          s['miniature'],
          s[`miniature-${miniatureClassname}`]
        )}
      >
        {isCoreOnlyModule && <div className={s['core-badge']} />}
      </div>
      <div className={s['title']}>{title}</div>
      <div className={s['description']}>{description}</div>
      {!hasCoreMembership && isCoreOnlyModule && (
        <div className={s['core-upsell-footer']}>
          {/* prettier-ignore */}
          <Trans i18nKey="widgets.coreUpsell.becomeCore.toAddSection" count={coreLimit}>
            Become <div className={s['core-banner']} /> to add up to {{coreLimit}} sections.
          </Trans>
        </div>
      )}
      {hasCoreMembership && isCoreOnlyModule && (
        <div className={s['core-upsell-footer']}>
          {/* prettier-ignore */}
          <Trans i18nKey="widgets.coreUpsell.addSection" count={coreLimit}>
            Add up to {{coreLimit}} sections as a <div className={s['core-banner']}  />
          </Trans>
        </div>
      )}
      {isLocked && (
        <div className={s['core-upsell-overlay']}>
          <div className={s['core-upsell-overlay-text']}>
            {/* prettier-ignore */}
            <Trans i18nKey="widgets.coreUpsell.becomeCore.toUnlock">
              Become <div className={s['core-banner']} /> to unlock
            </Trans>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetTypeCard;
